import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { ShipCoordinatesService } from './ship-coordinates.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  EnrichmentGuidePOI,
  EnrichmentGuidePOIResponse,
  EnrichmentGuidePageData,
} from '../Models/enrichment-guide-poi';
import { ResultStatus } from '../Models/common';

@Injectable({
  providedIn: 'root'
})
export class EnrichmentGuidePOIPCSNearShipsService {
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private appConfigService: AppConfigService,
    private httpClient: HttpClient,
    private shipCoordinatesService: ShipCoordinatesService,
  ) { }

  public loadPageData(shpID: number | undefined): Observable<EnrichmentGuidePageData> {
    return forkJoin({
      poi: this.fetchPOIData(shpID),
      shipCoordData: this.fetchShipCoordinates(shpID),
    }).pipe(
      map(({ poi, shipCoordData }) => ({
        ...poi,
        shipCoordData,
      }))
    );
  }

  private fetchPOIData(shpID: number | undefined): Observable<Omit<EnrichmentGuidePageData, 'shipCoordData'>> {
    return this.httpClient.post<EnrichmentGuidePOIResponse>(
      `${this.appConfigService.OBPortalSvcURL}GetEnrichmentGuidePOIPCSNearShips`,
      { shpID },
      { headers: this.headers }
    ).pipe(
      map((response) => this.parsePOIResponse(response)),
      catchError(() => of({
        statusR: true,
        nearbypointsofinterest: [] as EnrichmentGuidePOI[],
        visiblepointsofinterest: [] as EnrichmentGuidePOI[],
        interesttopics: [] as EnrichmentGuidePOI[],
      }))
    );
  }

  private parsePOIResponse(response: EnrichmentGuidePOIResponse): Omit<EnrichmentGuidePageData, 'shipCoordData'> {
    const result = response?.GetEnrichmentGuidePOIPCSNearShips;

    if (result?.Result?.Status === ResultStatus.Accepted) {
      const allItems = result.EnrichmentGuidePOIPCSNearShipsList ?? [];

      return {
        statusR: false,
        nearbypointsofinterest: allItems.filter(
          (poi) => poi.isNearby === 1 && poi.egpType === 'Point of Interest'
        ),
        visiblepointsofinterest: allItems.filter(
          (poi) => poi.isVisible === 1 && poi.isNearby === 0 && poi.egpType === 'Point of Interest'
        ),
        interesttopics: allItems.filter(
          (poi) => poi.isVisible === 1 && poi.egpType === 'Interest Topic'
        ),
      };
    }

    return {
      statusR: true,
      nearbypointsofinterest: [],
      visiblepointsofinterest: [],
      interesttopics: [],
    };
  }

  private fetchShipCoordinates(shpID: number | undefined): Observable<any> {
    return this.shipCoordinatesService.getShipCoordinates(shpID).pipe(
      map((data: any) => {
        if (data?.GetShipCoordinates?.Result?.Status === ResultStatus.Accepted) {
          return data.GetShipCoordinates.ShipCoordinates;
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }
}
